import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient, useQueries } from '@tanstack/react-query';

import { getPortalEnv } from '../../../../api/axiosClient';
import { useCurrentUser } from '../../../../auth/useCurrentUser';
import { PERMISSION_META } from '../../../../auth/permissionCatalog';
import { EnvPill, WriteButton } from '../../../../components/ui';
import { useEnv } from '../../../../context/EnvContext';
import { colors, primaryButtonSx, secondaryButtonSx, typo } from '../../../../theme/tokens';

import { applySyncPlan } from './applyPlan';
import { buildSyncPlan } from './diffEngine';
import { fetchEnvSnapshot } from './snapshot';
import { ApplyReport, ApplySections, SyncPlan } from './types';

interface Props {
  onNotify: (msg: string, sev: 'success' | 'error' | 'info') => void;
}

const cardSx = {
  border: `1px solid ${colors.borderLight}`,
  borderRadius: '12px',
  p: 2.5,
  mb: 2,
};

const sectionSx = {
  border: `1px solid ${colors.borderLight}`,
  borderRadius: '8px',
  p: 1.5,
  mb: 1.5,
};

const CountChip: React.FC<{ label: string; count: number; color?: 'success' | 'warning' | 'error' | 'default' }> = ({
  label,
  count,
  color = 'default',
}) => {
  if (count === 0) return null;
  return (
    <Chip
      size="small"
      label={`${label} ${count}`}
      color={color === 'default' ? undefined : color}
      sx={
        color === 'default'
          ? { height: 22, fontSize: 11, fontWeight: 600, backgroundColor: colors.bgMuted, color: colors.textSecondary }
          : { height: 22, fontSize: 11, fontWeight: 700 }
      }
    />
  );
};

const CodeRow: React.FC<{ code: string; label?: string; tag?: string; tagColor?: string; warn?: string }> = ({
  code,
  label,
  tag,
  tagColor,
  warn,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, flexWrap: 'wrap' }}>
    <Typography sx={{ fontFamily: 'monospace', fontSize: 12, color: colors.textPrimary, fontWeight: 600 }}>
      {code}
    </Typography>
    {label && <Typography sx={{ ...typo.tinyText }}>{label}</Typography>}
    {tag && (
      <Chip
        size="small"
        label={tag}
        sx={{ height: 18, fontSize: 10, fontWeight: 700, backgroundColor: tagColor ?? colors.bgMuted }}
      />
    )}
    {warn && (
      <Typography sx={{ ...typo.tinyText, color: colors.warningText }}>{warn}</Typography>
    )}
  </Box>
);

const resultRows = (
  results: Record<string, { ok: boolean; error_code?: string; error_message?: string }>,
): React.ReactNode => {
  const failures = Object.entries(results).filter(([, r]) => !r.ok);
  if (failures.length === 0) return null;
  return (
    <Box sx={{ mt: 1 }}>
      {failures.map(([key, r]) => (
        <Typography key={key} sx={{ ...typo.tinyText, color: colors.errorText }}>
          {key}: {r.error_code ?? 'error'} — {r.error_message}
        </Typography>
      ))}
    </Box>
  );
};

const reportSummary = (report: ApplyReport): { ok: number; failed: number } => {
  let ok = 0;
  let failed = 0;
  const buckets = [
    report.roles.create,
    report.roles.update,
    report.users.invite,
    report.users.reassign,
    report.groups.create,
    report.groups.update,
    report.userGroups.assign,
    report.userGroups.unassign,
  ];
  for (const bucket of buckets) {
    for (const r of Object.values(bucket)) {
      if (r.ok) ok += 1;
      else failed += 1;
    }
  }
  return { ok, failed };
};

/**
 * Access Sync (CPT-1135) — promotes access data (roles, users, tenant
 * groups) from THIS env (the one the portal is running in) to selected
 * target envs. Source wins on differences for matched entities; the sync
 * never deletes a target-only role or group (reported, left alone).
 */
const AccessSyncPanel: React.FC<Props> = ({ onNotify }) => {
  const qc = useQueryClient();
  const { hasPermission } = useCurrentUser();
  const { availableEnvs } = useEnv();
  const portalEnv = useMemo(() => getPortalEnv(), []);

  // PROD is only ever offered as a target when the operator holds
  // `prod_option` — mirrors the product spec's gate literally, on top of
  // whatever `useEnv().availableEnvs` already filtered upstream (defense in
  // depth; this component doesn't rely on that filtering alone).
  const targets = useMemo(
    () =>
      availableEnvs.filter(
        (e) => e.name !== portalEnv && (e.name !== 'prod' || hasPermission('prod_option')),
      ),
    [availableEnvs, portalEnv, hasPermission],
  );

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [requested, setRequested] = useState<string[]>([]);
  const [reports, setReports] = useState<Record<string, ApplyReport | undefined>>({});
  const [prodConfirm, setProdConfirm] = useState<{ env: string; plan: SyncPlan; sections: ApplySections } | null>(
    null,
  );
  const [confirmInput, setConfirmInput] = useState('');

  const toggleSelected = (env: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(env)) next.delete(env);
      else next.add(env);
      return next;
    });
  };

  const loadDiff = (env: string) => {
    setRequested((prev) => (prev.includes(env) ? prev : [...prev, env]));
  };

  const sourceQuery = useQuery({
    queryKey: ['access-sync', 'snapshot', portalEnv],
    queryFn: () => fetchEnvSnapshot(portalEnv),
    enabled: requested.length > 0,
    staleTime: 30_000,
  });

  const targetQueries = useQueries({
    queries: requested.map((env) => ({
      queryKey: ['access-sync', 'snapshot', env],
      queryFn: () => fetchEnvSnapshot(env),
      staleTime: 30_000,
    })),
  });

  const plansByEnv = useMemo(() => {
    const map: Record<string, SyncPlan | undefined> = {};
    if (!sourceQuery.data) return map;
    requested.forEach((env, i) => {
      const t = targetQueries[i];
      if (t?.data) map[env] = buildSyncPlan(sourceQuery.data!, t.data);
    });
    return map;
  }, [sourceQuery.data, targetQueries, requested]);

  const applyMutation = useMutation({
    mutationFn: (args: { env: string; plan: SyncPlan; sections: ApplySections }) =>
      applySyncPlan(args.env, args.plan, args.sections),
    onSuccess: (report, vars) => {
      setReports((prev) => ({ ...prev, [vars.env]: report }));
      const { ok, failed } = reportSummary(report);
      onNotify(
        failed > 0
          ? `Applied to ${vars.env.toUpperCase()} — ${ok} ok, ${failed} failed. See details below.`
          : `Applied to ${vars.env.toUpperCase()} — ${ok} change${ok === 1 ? '' : 's'} synced.`,
        failed > 0 ? 'error' : 'success',
      );
      qc.invalidateQueries({ queryKey: ['access-sync', 'snapshot', vars.env] });
    },
    onError: (_err, vars) => onNotify(`Failed to apply to ${vars.env.toUpperCase()}.`, 'error'),
  });

  const requestApply = (env: string, plan: SyncPlan, sections: ApplySections) => {
    if (env === 'prod') {
      setConfirmInput('');
      setProdConfirm({ env, plan, sections });
      return;
    }
    applyMutation.mutate({ env, plan, sections });
  };

  const confirmProdApply = () => {
    if (!prodConfirm) return;
    applyMutation.mutate(prodConfirm);
    setProdConfirm(null);
  };

  return (
    <Box>
      <Alert severity="info" sx={{ borderRadius: '8px', mb: 2 }}>
        Promotes roles, users, and tenant groups from <b>{portalEnv.toUpperCase()}</b> (this portal) to the
        target env(s) you pick below. On a match, {portalEnv.toUpperCase()} wins. Target-only roles and
        groups are reported, never deleted — nobody is ever deactivated or removed.
      </Alert>

      <Typography sx={{ ...typo.sectionLabel, mb: 1 }}>Targets</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {targets.map((t) => (
          <FormControlLabel
            key={t.name}
            control={
              <Checkbox size="small" checked={selected.has(t.name)} onChange={() => toggleSelected(t.name)} />
            }
            label={<EnvPill env={t.name} size="sm" />}
            sx={{ border: `1px solid ${colors.borderLight}`, borderRadius: '999px', pr: 1.5, ml: 0 }}
          />
        ))}
        {targets.length === 0 && (
          <Typography sx={typo.secondaryText}>No other envs are available to sync to.</Typography>
        )}
      </Box>

      {Array.from(selected).map((env) => {
        const loaded = requested.includes(env);
        const idx = requested.indexOf(env);
        const targetQuery = idx >= 0 ? targetQueries[idx] : undefined;
        const loading = sourceQuery.isLoading || targetQuery?.isLoading;
        const failedLoad = sourceQuery.isError || targetQuery?.isError;
        const plan = plansByEnv[env];
        const report = reports[env];
        const applyingThisEnv = applyMutation.isPending && applyMutation.variables?.env === env;

        return (
          <Box key={env} sx={cardSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EnvPill env={env} />
                {report && (
                  <Typography sx={{ ...typo.tinyText }}>
                    Last applied — {reportSummary(report).ok} ok, {reportSummary(report).failed} failed
                  </Typography>
                )}
              </Box>
              <Button
                size="small"
                variant="outlined"
                sx={secondaryButtonSx}
                onClick={() => {
                  loadDiff(env);
                  if (loaded) {
                    qc.invalidateQueries({ queryKey: ['access-sync', 'snapshot', env] });
                    qc.invalidateQueries({ queryKey: ['access-sync', 'snapshot', portalEnv] });
                  }
                }}
              >
                {loaded ? 'Refresh diff' : 'Load diff'}
              </Button>
            </Box>

            {!loaded ? null : loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} sx={{ color: colors.adminAccent }} />
              </Box>
            ) : failedLoad ? (
              <Alert severity="error" sx={{ borderRadius: '8px' }}>
                Couldn&apos;t load the diff for {env.toUpperCase()} — try again.
              </Alert>
            ) : !plan ? null : (
              <>
                {/* Section 1 — Permission catalog (compare-only). */}
                <Box sx={sectionSx}>
                  <Typography sx={{ ...typo.sectionTitle, fontSize: 13, mb: 0.5 }}>Permission catalog</Typography>
                  {plan.catalogGaps.length === 0 ? (
                    <Typography sx={{ ...typo.tinyText, color: colors.successText }}>In sync.</Typography>
                  ) : (
                    <Alert severity="warning" sx={{ borderRadius: '8px' }}>
                      {env.toUpperCase()} needs migrations applied — missing permission code
                      {plan.catalogGaps.length === 1 ? '' : 's'}:{' '}
                      {plan.catalogGaps
                        .map((g) => `${PERMISSION_META[g.missingCode]?.label ?? g.missingCode} (${g.missingCode})`)
                        .join(', ')}
                      . Affected roles are blocked until then:{' '}
                      {Array.from(new Set(plan.catalogGaps.flatMap((g) => g.affectedRoles))).join(', ')}.
                    </Alert>
                  )}
                </Box>

                {/* Section 2 — Roles. */}
                <Box sx={sectionSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ ...typo.sectionTitle, fontSize: 13 }}>Roles</Typography>
                    <WriteButton
                      ignoreEnvReadOnly
                      size="small"
                      variant="contained"
                      sx={primaryButtonSx}
                      disabled={
                        applyingThisEnv || (plan.roles.create.length === 0 && plan.roles.update.length === 0)
                      }
                      onClick={() => requestApply(env, plan, { roles: true, users: false, groups: false })}
                    >
                      Apply roles
                    </WriteButton>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                    <CountChip label="Create" count={plan.roles.create.length} color="success" />
                    <CountChip label="Update" count={plan.roles.update.length} color="warning" />
                    <CountChip
                      label="Blocked"
                      count={plan.roles.create.filter((r) => r.blocked).length + plan.roles.update.filter((r) => r.blocked).length}
                      color="error"
                    />
                    <CountChip label="Target-only (left alone)" count={plan.roles.targetOnly.length} />
                  </Box>
                  {plan.roles.create.map((r) => (
                    <CodeRow
                      key={`c-${r.role_code}`}
                      code={r.role_code}
                      label={r.role_name}
                      tag={r.blocked ? 'blocked' : 'create'}
                      tagColor={r.blocked ? colors.errorBgStrong : colors.successBgStrong}
                    />
                  ))}
                  {plan.roles.update.map((r) => (
                    <CodeRow
                      key={`u-${r.role_code}`}
                      code={r.role_code}
                      label={r.role_name}
                      tag={r.blocked ? 'blocked' : 'update'}
                      tagColor={r.blocked ? colors.errorBgStrong : colors.warningBgStrong}
                    />
                  ))}
                  {report && resultRows(report.roles.create)}
                  {report && resultRows(report.roles.update)}
                </Box>

                {/* Section 3 — Users. */}
                <Box sx={sectionSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ ...typo.sectionTitle, fontSize: 13 }}>Users</Typography>
                    <WriteButton
                      ignoreEnvReadOnly
                      size="small"
                      variant="contained"
                      sx={primaryButtonSx}
                      disabled={
                        applyingThisEnv || (plan.users.invite.length === 0 && plan.users.reassign.length === 0)
                      }
                      onClick={() => requestApply(env, plan, { roles: false, users: true, groups: false })}
                    >
                      Apply users
                    </WriteButton>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                    <CountChip label="Invite" count={plan.users.invite.length} color="success" />
                    <CountChip label="Reassign role" count={plan.users.reassign.length} color="warning" />
                    <CountChip label="Skipped (inactive in source)" count={plan.users.unmatched.length} />
                    <CountChip label="Target-only (left alone)" count={plan.users.targetOnly.length} />
                  </Box>
                  {plan.users.invite.map((u) => (
                    <CodeRow key={`inv-${u.email}`} code={u.email} tag="invite" tagColor={colors.successBgStrong} />
                  ))}
                  {plan.users.reassign.map((u) => (
                    <CodeRow
                      key={`re-${u.email}`}
                      code={u.email}
                      label={`${u.from_role_code} → ${u.to_role_code}`}
                      tag="reassign"
                      tagColor={colors.warningBgStrong}
                    />
                  ))}
                  {report && resultRows(report.users.invite)}
                  {report && resultRows(report.users.reassign)}
                </Box>

                {/* Section 4 — Tenant groups (+ user↔group assignments). */}
                <Box sx={sectionSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ ...typo.sectionTitle, fontSize: 13 }}>Tenant groups</Typography>
                    <WriteButton
                      ignoreEnvReadOnly
                      size="small"
                      variant="contained"
                      sx={primaryButtonSx}
                      disabled={
                        applyingThisEnv ||
                        (plan.groups.create.length === 0 &&
                          plan.groups.update.length === 0 &&
                          plan.userGroups.assign.length === 0 &&
                          plan.userGroups.unassign.length === 0)
                      }
                      onClick={() => requestApply(env, plan, { roles: false, users: false, groups: true })}
                    >
                      Apply groups
                    </WriteButton>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                    <CountChip label="Create" count={plan.groups.create.length} color="success" />
                    <CountChip label="Update" count={plan.groups.update.length} color="warning" />
                    <CountChip label="Skipped members" count={plan.groups.skippedMembers.length} color="error" />
                    <CountChip label="Target-only (left alone)" count={plan.groups.targetOnly.length} />
                    <CountChip label="Assign membership" count={plan.userGroups.assign.length} color="success" />
                    <CountChip label="Unassign membership" count={plan.userGroups.unassign.length} color="warning" />
                  </Box>
                  {plan.groups.create.map((g) => (
                    <CodeRow key={`gc-${g.name}`} code={g.name} tag="create" tagColor={colors.successBgStrong} />
                  ))}
                  {plan.groups.update.map((g) => (
                    <CodeRow key={`gu-${g.name}`} code={g.name} tag="update" tagColor={colors.warningBgStrong} />
                  ))}
                  {plan.groups.skippedMembers.map((m, i) => (
                    <CodeRow
                      key={`skip-${i}`}
                      code={m.scope_code}
                      label={`${m.group_name} · ${m.scope_type}`}
                      warn="not found on target — skipped"
                    />
                  ))}
                  {report && resultRows(report.groups.create)}
                  {report && resultRows(report.groups.update)}
                  {report && resultRows(report.userGroups.assign)}
                  {report && resultRows(report.userGroups.unassign)}
                </Box>
              </>
            )}
          </Box>
        );
      })}

      <Dialog open={!!prodConfirm} onClose={() => setProdConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ ...typo.sectionTitle }}>Apply to PROD?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14, color: colors.textSecondary, mb: 1.5 }}>
            You&apos;re about to write access changes to <b>production</b>. Type <b>prod</b> to confirm.
          </DialogContentText>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="prod"
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setProdConfirm(null)} variant="outlined" sx={secondaryButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={confirmProdApply}
            disabled={confirmInput.trim().toLowerCase() !== 'prod'}
            variant="contained"
            sx={{ ...primaryButtonSx, backgroundColor: colors.errorIcon, color: colors.bgWhite, '&:hover': { backgroundColor: colors.errorText } }}
          >
            Apply to PROD
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccessSyncPanel;
